import { useEffect, useState, useMemo, useCallback } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import DragDropMonthCalendar from "../components/calendar/DragDropMonthCalendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Dumbbell,
  ClipboardList,
  MapPin,
  Clock,
  Plus,
  CalendarPlus,
  Users,
  Trophy,
  PartyPopper,
  Trash2,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "../components/ui/date-picker";

const EVENT_TYPES = [
  { value: "meeting", label: "Riunione", icon: Users, color: "bg-amber-50 text-amber-600", dot: "bg-amber-500" },
  { value: "tournament", label: "Torneo", icon: Trophy, color: "bg-sky-50 text-sky-600", dot: "bg-sky-500" },
  { value: "social", label: "Evento sociale", icon: PartyPopper, color: "bg-pink-50 text-pink-600", dot: "bg-pink-500" },
  { value: "other", label: "Altro", icon: CalendarPlus, color: "bg-slate-100 text-slate-600", dot: "bg-slate-500" },
];

const emptyForm = {
  type: "meeting",
  title: "",
  date: "",
  time: "",
  location: "",
  field_id: "",
  notes: "",
};

function getTypeMeta(type) {
  return EVENT_TYPES.find((t) => t.value === type) || EVENT_TYPES[3];
}

export default function CalendarPage() {
  const { activeTeam, canEdit, season } = useTeams();
  const [events, setEvents] = useState([]);
  const [fields, setFields] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const [c, f] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/calendar`, { params: { season } }),
      api.get(`/teams/${activeTeam.id}/fields`).catch(() => ({ data: [] })),
    ]);
    setEvents(c.data.events || []);
    setFields(f.data || []);
  }, [activeTeam, season]);

  useEffect(() => {
    load();
  }, [load]);

  const eventsByDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (!e.date) return;
      (map[e.date] ||= []).push(e);
    });
    return map;
  }, [events]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const selectedKey = selected
    ? `${selected.getFullYear()}-${String(selected.getMonth() + 1).padStart(2, "0")}-${String(selected.getDate()).padStart(2, "0")}`
    : "";
  const dayEvents = eventsByDate[selectedKey] || [];

  const newEvent = () => {
    setForm({ ...emptyForm, date: selectedKey || "" });
    setEditingId(null);
    setOpen(true);
  };

  const editEvent = (e) => {
    setForm({
      type: e.type || "meeting",
      title: e.title || "",
      date: e.date || "",
      time: e.time || "",
      location: e.location || "",
      field_id: e.field_id || "",
      notes: e.notes || "",
    });
    setEditingId(e.id);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Titolo richiesto");
    if (!form.date) return toast.error("Data richiesta");
    const payload = { ...form, team_id: activeTeam.id, season };
    if (!payload.field_id) delete payload.field_id;
    if (editingId) await api.put(`/events/${editingId}`, payload);
    else await api.post(`/events`, payload);
    toast.success("Evento salvato");
    setOpen(false);
    setForm(emptyForm);
    setEditingId(null);
    load();
  };

  const remove = async (e) => {
    if (!window.confirm("Eliminare evento?")) return;
    await api.delete(`/events/${e.id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Calendario</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Eventi della stagione</h1>
          <p className="text-slate-500">{events.length} eventi totali</p>
        </div>
        {canEdit && (
          <Button
            onClick={newEvent}
            className="rounded-full bg-sky-500 hover:bg-sky-600"
            data-testid="new-event-btn"
          >
            <Plus size={16} className="mr-1" /> Nuovo evento
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
        <DragDropMonthCalendar
          events={events}
          canEdit={canEdit}
          onReload={load}
          selectedDate={selected}
          onSelectDate={setSelected}
        />

        <Card className="p-6 border-slate-200">
          <div className="label-eyebrow mb-1">
            {selected
              ? selected.toLocaleDateString("it-IT", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })
              : "Seleziona un giorno"}
          </div>
          <h2 className="font-heading text-2xl font-semibold mb-4">
            {dayEvents.length === 0 ? "Nessun evento" : `${dayEvents.length} eventi`}
          </h2>
          <div className="space-y-3">
            {dayEvents.map((e) => {
              const isTraining = e.type === "training";
              const isMatch = e.type === "match";
              const isCustom = !isTraining && !isMatch;
              const meta = isCustom ? getTypeMeta(e.type) : null;
              const Icon = isTraining ? Dumbbell : isMatch ? ClipboardList : meta.icon;
              const bgIcon = isTraining
                ? "bg-violet-50 text-violet-600"
                : isMatch
                ? "bg-emerald-50 text-emerald-600"
                : meta.color;
              return (
                <div
                  key={`${e.type}-${e.id}`}
                  className="flex items-start gap-4 p-4 rounded-xl border border-slate-200"
                  data-testid={`calendar-event-${e.id}`}
                >
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${bgIcon}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold flex items-center gap-2 flex-wrap">
                      {e.title}
                      {isCustom && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-bold">
                          {meta.label}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-500 mt-1 flex flex-wrap gap-3">
                      {e.opponent && <span>vs {e.opponent}</span>}
                      {e.meeting_time && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> Ritrovo {e.meeting_time}
                        </span>
                      )}
                      {e.time && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {e.time}
                        </span>
                      )}
                      {e.duration_min && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} /> {e.duration_min} min
                        </span>
                      )}
                      {e.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={12} /> {e.location}
                        </span>
                      )}
                      {e.player_count != null && <span>{e.player_count} convocati</span>}
                    </div>
                    {e.notes && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{e.notes}</p>
                    )}
                  </div>
                  {isCustom && canEdit && (
                    <div className="flex gap-0.5">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => editEvent(e)}
                        data-testid={`edit-event-${e.id}`}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(e)}>
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            {dayEvents.length === 0 && (
              <p className="text-slate-400 text-sm">
                Nessun evento in questa data. Crea un evento con il pulsante in alto.
              </p>
            )}
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? "Modifica evento" : "Nuovo evento"}</DialogTitle>
          <DialogDescription className="sr-only">Crea o modifica un evento sul calendario della squadra.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Titolo</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Es. Riunione genitori"
                data-testid="event-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data</Label>
                <DatePicker
                  value={form.date}
                  onChange={(v) => setForm({ ...form, date: v })}
                  data-testid="event-date-input"
                />
              </div>
              <div>
                <Label>Ora</Label>
                <Input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Luogo</Label>
              {fields.length > 0 ? (
                <Select
                  value={form.field_id || "__none__"}
                  onValueChange={(v) => {
                    if (v === "__none__") return setForm({ ...form, field_id: "" });
                    const f = fields.find((x) => x.id === v);
                    setForm({ ...form, field_id: v, location: f ? f.name : form.location });
                  }}
                >
                  <SelectTrigger className="mb-2">
                    <SelectValue placeholder="Seleziona campo salvato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Inserisci manualmente —</SelectItem>
                    {fields.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Indirizzo o nome luogo"
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="save-event-btn"
            >
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
